/**
 * Offline Database Layer — IndexedDB abstraction
 * Stores cached API responses for offline viewing.
 * Sensitive fields (cpf, rg, encrypted columns) are NEVER stored.
 *
 * Cache eviction strategy:
 * - TTL: entries older than CACHE_TTL_MS are evicted (default 7 days)
 * - LRU: least-recently-accessed entries evicted when over MAX_CACHE_ENTRIES
 * - Size cap: estimated ~50MB total; evicts oldest-accessed entries when exceeded
 *
 * Resilient: all operations silently degrade when IndexedDB is
 * unavailable (private browsing, quota exceeded, old browsers).
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

// ─── Eviction constants ───────────────────────────────────
export const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
export const MAX_CACHE_ENTRIES = 500;
export const MAX_CACHE_BYTES = 50 * 1024 * 1024; // ~50 MB

// ─── Schema ───────────────────────────────────────────────
interface OfflineDBSchema extends DBSchema {
  cache: {
    key: string;
    value: {
      key: string;
      data: unknown;
      updatedAt: number;
      lastAccessedAt: number;
      sizeBytes: number;
      profile: string;
    };
    indexes: {
      'by-profile': string;
      'by-last-accessed': number;
      'by-updated': number;
    };
  };
  offlineQueue: {
    key: string;
    value: OfflineAction;
    indexes: { 'by-status': string; 'by-created': number };
  };
}

export interface OfflineAction {
  id: string;
  action: 'insert' | 'update' | 'upsert';
  table: string;
  payload: Record<string, unknown> | Record<string, unknown>[];
  createdAt: number;
  status: 'pending' | 'syncing' | 'failed' | 'done';
  retries: number;
  errorMessage?: string;
}

const DB_NAME = 'submission-bjj-offline';
const DB_VERSION = 2;

let dbPromise: Promise<IDBPDatabase<OfflineDBSchema>> | null = null;
/** Set to true if IndexedDB is confirmed unavailable */
let idbUnavailable = false;

function isIdbSupported(): boolean {
  if (idbUnavailable) return false;
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    idbUnavailable = true;
    return false;
  }
}

function getDb(): Promise<IDBPDatabase<OfflineDBSchema>> {
  if (!isIdbSupported()) {
    return Promise.reject(new Error('IndexedDB not available'));
  }
  if (!dbPromise) {
    dbPromise = openDB<OfflineDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // --- Cache store ---
        if (oldVersion < 1) {
          const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
          cacheStore.createIndex('by-profile', 'profile');
          cacheStore.createIndex('by-last-accessed', 'lastAccessedAt');
          cacheStore.createIndex('by-updated', 'updatedAt');
        } else if (oldVersion < 2) {
          // Migrating from v1 → v2: delete and recreate with new indexes
          if (db.objectStoreNames.contains('cache')) {
            db.deleteObjectStore('cache');
          }
          const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
          cacheStore.createIndex('by-profile', 'profile');
          cacheStore.createIndex('by-last-accessed', 'lastAccessedAt');
          cacheStore.createIndex('by-updated', 'updatedAt');
        }

        // --- Queue store ---
        if (oldVersion < 1) {
          const queueStore = db.createObjectStore('offlineQueue', { keyPath: 'id' });
          queueStore.createIndex('by-status', 'status');
          queueStore.createIndex('by-created', 'createdAt');
        }
      },
      blocked() {
        console.warn('[OfflineDB] DB blocked by another tab');
      },
      blocking() {
        console.warn('[OfflineDB] Blocking newer version');
      },
      terminated() {
        console.warn('[OfflineDB] DB connection terminated');
        dbPromise = null;
      },
    }).catch((err) => {
      console.warn('[OfflineDB] Failed to open IndexedDB:', err.message);
      idbUnavailable = true;
      dbPromise = null;
      throw err;
    });
  }
  return dbPromise;
}

// ─── Sensitive fields to strip before caching ─────────────
const SENSITIVE_FIELDS = new Set([
  'cpf', 'cpf_encrypted', 'rg', 'rg_encrypted',
  'exame_medico_url_encrypted', 'email_encrypted',
  'telefone_encrypted', 'data_nascimento_encrypted',
  'endereco_encrypted', 'responsavel_nome_encrypted',
  'responsavel_telefone_encrypted', 'alergias_encrypted',
  'contato_emergencia_nome_encrypted', 'contato_emergencia_telefone_encrypted',
  'medicamentos_uso_encrypted', 'observacoes_medicas_encrypted',
  'hospital_preferencia_encrypted', 'outras_condicoes_encrypted',
  'restricoes_lesoes_encrypted', 'aceite_ip_encrypted',
  'aceite_nome_completo_encrypted', 'ip_address_encrypted',
  'user_email_encrypted',
]);

function stripSensitive(data: unknown): unknown {
  if (Array.isArray(data)) {
    return data.map(stripSensitive);
  }
  if (data && typeof data === 'object') {
    const cleaned: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      if (!SENSITIVE_FIELDS.has(k)) {
        cleaned[k] = v;
      }
    }
    return cleaned;
  }
  return data;
}

/** Estimate size in bytes of a value (rough but fast) */
function estimateSize(data: unknown): number {
  try {
    const str = JSON.stringify(data);
    // UTF-16 in JS, but stored as UTF-8 in IDB; approximate
    return new Blob([str]).size;
  } catch {
    return 1024; // fallback 1KB
  }
}

// ─── Cache API (all silently degrade) ─────────────────────
export async function setCache(key: string, data: unknown, profile: string): Promise<void> {
  try {
    const db = await getDb();
    const sanitized = stripSensitive(data);
    const now = Date.now();
    await db.put('cache', {
      key,
      data: sanitized,
      updatedAt: now,
      lastAccessedAt: now,
      sizeBytes: estimateSize(sanitized),
      profile,
    });
  } catch {
    // Silently ignore — cache is best-effort
  }
}

export async function getCache<T = unknown>(key: string): Promise<T | null> {
  try {
    const db = await getDb();
    const entry = await db.get('cache', key);
    if (!entry) return null;

    // Touch lastAccessedAt for LRU (fire-and-forget)
    db.put('cache', { ...entry, lastAccessedAt: Date.now() }).catch(() => {});

    return entry.data as T;
  } catch {
    return null;
  }
}

export async function clearCacheByProfile(profile: string): Promise<void> {
  try {
    const db = await getDb();
    const keys = await db.getAllKeysFromIndex('cache', 'by-profile', profile);
    const tx = db.transaction('cache', 'readwrite');
    await Promise.all(keys.map((k) => tx.store.delete(k)));
    await tx.done;
  } catch {
    // Ignore
  }
}

export async function clearAllCache(): Promise<void> {
  try {
    const db = await getDb();
    await db.clear('cache');
  } catch {
    // Ignore
  }
}

// ─── Eviction API ─────────────────────────────────────────

export interface EvictionStats {
  ttlEvicted: number;
  lruEvicted: number;
  sizeEvicted: number;
  totalRemaining: number;
  totalSizeBytes: number;
}

/**
 * Run full cache maintenance:
 * 1. TTL eviction — remove entries older than CACHE_TTL_MS
 * 2. LRU eviction — keep at most MAX_CACHE_ENTRIES
 * 3. Size eviction — keep total under MAX_CACHE_BYTES
 */
export async function evictStaleCache(): Promise<EvictionStats> {
  const stats: EvictionStats = {
    ttlEvicted: 0,
    lruEvicted: 0,
    sizeEvicted: 0,
    totalRemaining: 0,
    totalSizeBytes: 0,
  };

  try {
    const db = await getDb();

    // Step 1: TTL eviction — delete anything not updated in CACHE_TTL_MS
    const cutoff = Date.now() - CACHE_TTL_MS;
    const allEntries = await db.getAll('cache');
    const expired = allEntries.filter((e) => e.updatedAt < cutoff);

    if (expired.length > 0) {
      const tx1 = db.transaction('cache', 'readwrite');
      await Promise.all(expired.map((e) => tx1.store.delete(e.key)));
      await tx1.done;
      stats.ttlEvicted = expired.length;
    }

    // Step 2: LRU eviction — if count > MAX, remove least-recently-accessed
    let remaining = await db.getAll('cache');

    if (remaining.length > MAX_CACHE_ENTRIES) {
      // Sort by lastAccessedAt ascending (oldest first)
      remaining.sort((a, b) => a.lastAccessedAt - b.lastAccessedAt);
      const toRemove = remaining.slice(0, remaining.length - MAX_CACHE_ENTRIES);
      const tx2 = db.transaction('cache', 'readwrite');
      await Promise.all(toRemove.map((e) => tx2.store.delete(e.key)));
      await tx2.done;
      stats.lruEvicted = toRemove.length;
      remaining = remaining.slice(toRemove.length);
    }

    // Step 3: Size eviction — if total > MAX_CACHE_BYTES, remove LRU until under
    let totalSize = remaining.reduce((sum, e) => sum + (e.sizeBytes || 0), 0);

    if (totalSize > MAX_CACHE_BYTES) {
      // Sort by lastAccessedAt ascending
      remaining.sort((a, b) => a.lastAccessedAt - b.lastAccessedAt);
      const keysToDelete: string[] = [];

      for (const entry of remaining) {
        if (totalSize <= MAX_CACHE_BYTES) break;
        totalSize -= entry.sizeBytes || 0;
        keysToDelete.push(entry.key);
        stats.sizeEvicted++;
      }

      if (keysToDelete.length > 0) {
        const tx3 = db.transaction('cache', 'readwrite');
        await Promise.all(keysToDelete.map((k) => tx3.store.delete(k)));
        await tx3.done;
      }
    }

    // Final stats
    const finalEntries = await db.getAll('cache');
    stats.totalRemaining = finalEntries.length;
    stats.totalSizeBytes = finalEntries.reduce((sum, e) => sum + (e.sizeBytes || 0), 0);
  } catch (err) {
    console.warn('[OfflineDB] Cache eviction error:', err);
  }

  return stats;
}

/**
 * Get current cache statistics (for dev panels / monitoring).
 */
export async function getCacheStats(): Promise<{
  entryCount: number;
  totalSizeBytes: number;
  oldestUpdatedAt: number | null;
  newestUpdatedAt: number | null;
}> {
  try {
    const db = await getDb();
    const all = await db.getAll('cache');
    if (all.length === 0) {
      return { entryCount: 0, totalSizeBytes: 0, oldestUpdatedAt: null, newestUpdatedAt: null };
    }
    const totalSize = all.reduce((sum, e) => sum + (e.sizeBytes || 0), 0);
    const times = all.map((e) => e.updatedAt).sort((a, b) => a - b);
    return {
      entryCount: all.length,
      totalSizeBytes: totalSize,
      oldestUpdatedAt: times[0],
      newestUpdatedAt: times[times.length - 1],
    };
  } catch {
    return { entryCount: 0, totalSizeBytes: 0, oldestUpdatedAt: null, newestUpdatedAt: null };
  }
}

// ─── Queue API (used by offlineQueue.ts) ──────────────────
export async function addToQueue(action: OfflineAction): Promise<void> {
  const db = await getDb(); // Let this throw — queue failures should surface
  await db.put('offlineQueue', action);
}

export async function getPendingActions(): Promise<OfflineAction[]> {
  try {
    const db = await getDb();
    return db.getAllFromIndex('offlineQueue', 'by-status', 'pending');
  } catch {
    return [];
  }
}

export async function getFailedActions(): Promise<OfflineAction[]> {
  try {
    const db = await getDb();
    return db.getAllFromIndex('offlineQueue', 'by-status', 'failed');
  } catch {
    return [];
  }
}

export async function getAllQueuedActions(): Promise<OfflineAction[]> {
  try {
    const db = await getDb();
    const all = await db.getAll('offlineQueue');
    return all.filter((a) => a.status !== 'done');
  } catch {
    return [];
  }
}

export async function updateQueueAction(id: string, updates: Partial<OfflineAction>): Promise<void> {
  try {
    const db = await getDb();
    const existing = await db.get('offlineQueue', id);
    if (existing) {
      await db.put('offlineQueue', { ...existing, ...updates });
    }
  } catch {
    // Ignore
  }
}

export async function removeFromQueue(id: string): Promise<void> {
  try {
    const db = await getDb();
    await db.delete('offlineQueue', id);
  } catch {
    // Ignore
  }
}

export async function clearCompletedActions(): Promise<void> {
  try {
    const db = await getDb();
    const all = await db.getAll('offlineQueue');
    const tx = db.transaction('offlineQueue', 'readwrite');
    await Promise.all(
      all.filter((a) => a.status === 'done').map((a) => tx.store.delete(a.id))
    );
    await tx.done;
  } catch {
    // Ignore
  }
}

export async function getQueueCount(): Promise<number> {
  try {
    const db = await getDb();
    const pending = await db.countFromIndex('offlineQueue', 'by-status', 'pending');
    const syncing = await db.countFromIndex('offlineQueue', 'by-status', 'syncing');
    const failed = await db.countFromIndex('offlineQueue', 'by-status', 'failed');
    return pending + syncing + failed;
  } catch {
    return 0;
  }
}

/**
 * Remove stale queue entries older than maxAgeMs.
 * Removes 'done' entries and 'failed' entries that exceeded MAX_RETRIES.
 */
export async function clearStaleQueueEntries(maxAgeMs: number): Promise<number> {
  try {
    const db = await getDb();
    const all = await db.getAll('offlineQueue');
    const cutoff = Date.now() - maxAgeMs;
    const stale = all.filter(
      (a) =>
        a.createdAt < cutoff &&
        (a.status === 'done' || (a.status === 'failed' && a.retries >= 5))
    );

    if (stale.length === 0) return 0;

    const tx = db.transaction('offlineQueue', 'readwrite');
    await Promise.all(stale.map((a) => tx.store.delete(a.id)));
    await tx.done;
    return stale.length;
  } catch {
    return 0;
  }
}
